import {Modal} from '@mantine/core';
import {Box} from 'src/design/atoms/Box';
import {Card} from 'src/design/atoms/Card';
import {Divider} from 'src/design/atoms/Divider';
import {Grid} from 'src/design/atoms/Grid';
import {Group} from 'src/design/atoms/Group';
import {Stack} from 'src/design/atoms/Stack';
import {Text} from 'src/design/atoms/Text';
import {Title} from 'src/design/atoms/Title';
import React, {useEffect, useMemo, useState} from 'react';
import type {VisibilityRule} from 'src/api/ProactApiClient';
import {CreateButton} from 'src/design/molecules/CreateButton';
import {Button} from 'src/design/atoms/Button';
import {DeleteButton} from 'src/design/molecules/DeleteButton';
import {Description} from 'src/design/atoms/Description';
import {ContentTitle} from 'src/design/atoms/Typography';
import {PendingIcon, SuccessIcon} from 'src/design/atoms/icons/StatusIcons';
import {SelectBox} from 'src/design/atoms/SelectBox';
import {FormField} from 'src/app/forms/FormField';
import {toSingleValueField} from 'src/app/forms/singleValueField';
import {showHideOptions} from 'src/shared/constants/fieldTypeDictionary';
import {isValidArray} from 'src/utilities/objectUtils';
import type {ISelectOption} from 'src/types/Form';
import type {IFormFieldV2} from 'src/types/FormV2';
import {VisibilutyRuleFieldTypeV2} from 'src/types/FormV2';
import {v4 as uuidv4} from 'uuid';
import {
  useGetFieldList,
  useGetSelectedField,
  useUpdateSelectedField,
} from '../../../../../hooks/useFormBuilder';
import {VisibilityImpactTextList} from './VisibilityImpactTextList';

export const VisibilityRuleModal = React.memo(() => {
  const fieldList = useGetFieldList();
  const field = useGetSelectedField();
  const updateSelectedField = useUpdateSelectedField();

  const [open, setOpen] = useState(false);

  const [rules, setRules] = useState<VisibilityRule[]>([]);

  const [fieldListOptions, setFieldListOptions] = useState<ISelectOption[]>([]);
  const [formFieldsV2, setFormFieldsV2] = useState<IFormFieldV2[]>([]);

  const [impactsList, setImpactsList] = useState<string[]>([]);
  const [impactedByList, setImpactedByList] = useState<string[]>([]);

  // Compute valid rules - a rule is valid if it has both a field and a value
  const validRules = useMemo(() => {
    return rules.filter(
      rule =>
        !!rule.targetFieldInvariantId &&
        rule.fieldValue !== '' &&
        rule.fieldValue !== null &&
        rule.fieldValue !== undefined
    );
  }, [rules]);

  useEffect(() => {
    if (open) {
      setRules(field?.visibility?.rules ?? []);
    }
  }, [field, open]);

  useEffect(() => {
    const impacts: string[] =
      field?.visibility?.rules
        ?.map(rule => {
          const matches = fieldList.filter(
            item => item.invariantId === rule.targetFieldInvariantId
          );

          if (
            matches === undefined ||
            matches.length !== 1 ||
            matches[0] === undefined
          ) {
            console.warn('Invalid matches found for field:, ', field, matches);
            return '';
          }

          const match = matches[0];

          return `Field: ${match.label}`;
        })
        .filter(item => item !== '') ?? [];

    setImpactsList(impacts);
  }, [field, fieldList]);

  useEffect(() => {
    if (!field) {
      return;
    }

    const impactedBy: string[] = fieldList
      .map(item => {
        const rules = item?.visibility?.rules?.filter(
          rule => rule.targetFieldInvariantId === field.invariantId
        );

        if (rules && rules.length > 0) {
          return item.label;
        }

        return '';
      })
      .filter(item => item !== '');

    setImpactedByList(impactedBy);

    const dataFieldListOptions = fieldList
      .filter(item => VisibilutyRuleFieldTypeV2.includes(item.type))
      .map(
        item =>
          ({
            id: item.invariantId,
            label: item.label.abbreviate(40),
          } as ISelectOption)
      );

    setFieldListOptions(dataFieldListOptions);

    setFormFieldsV2(fieldList);
  }, [fieldList, field]);

  const handleOpenDialog = React.useCallback(() => {
    setOpen(true);
  }, []);

  const handleCloseDialog = React.useCallback(() => {
    setOpen(false);
  }, []);

  const handleSaveSelections = React.useCallback(() => {
    if (!field) {
      return;
    }

    updateSelectedField({
      ...field,
      visibility: {
        ...field.visibility,
        rules: rules,
      },
    });

    setOpen(false);
  }, [field, rules, updateSelectedField]);

  const handleAddRule = React.useCallback(() => {
    setRules(prevState => [
      ...prevState,
      {
        offlineId: uuidv4(),
        targetFieldInvariantId: '',
        fieldValue: '',
        visibility: true,
      } as VisibilityRule,
    ]);
  }, []);

  const handleRemoveRule = React.useCallback(
    (rule: VisibilityRule) => {
      const filtered = rules.filter(item => item !== rule);
      setRules(filtered);
    },
    [rules]
  );

  const handleVisibilityChangeForRule = React.useCallback(
    (value: string, offlineId: string | undefined, ruleIndex: number) => {
      const currentRule = rules[ruleIndex];
      const rule = {
        ...currentRule,
        visibility: value === 'show' ? true : false,
      };
      const nodes = [...rules];
      nodes[ruleIndex] = rule;
      setRules(nodes);
    },
    [rules]
  );

  const handleFieldNameChangeForRule = React.useCallback(
    (value: string, offlineId: string | undefined, ruleIndex: number) => {
      const currentRule = rules[ruleIndex];
      const rule = {
        ...currentRule,
        targetFieldInvariantId: value,
      };

      const nodes = [...rules];
      nodes[ruleIndex] = rule;
      setRules(nodes);
    },
    [rules]
  );

  const handleFieldValueChangeForRule = React.useCallback(
    (_: any, value: any, ruleIndex: number) => {
      const currentRule = rules[ruleIndex];
      const rule = {
        ...currentRule,
        fieldValue: value?.toString() ?? '',
      };

      const nodes = [...rules];
      nodes[ruleIndex] = rule;
      setRules(nodes);
    },
    [rules]
  );

  if (!field) {
    return null;
  }

  return (
    <Box>
      <ContentTitle text="Field Visibility Rules" />

      <VisibilityImpactTextList
        title="Related Rules from Other Fields"
        list={impactedByList}
      />
      <VisibilityImpactTextList
        title="Rules for Current Field"
        list={impactsList}
      />

      <Button
        size="sm"
        color="black"
        variant="outline"
        onClick={() => handleOpenDialog()}>
        Configure Rules
      </Button>

      <Box style={{flexBasis: '100%', height: 0}} />
      <Modal
        opened={open}
        onClose={handleCloseDialog}
        title={<Title order={4}>Visibility Rules for: {field.label}</Title>}
        size="60%"
        zIndex={1000}>
        <Box>
          <ContentTitle text="Related Rules from Other Fields" />
          <Description
            size="xs"
            mt={0}
            mb="md"
            text="Visibility rules that are impacted by the current field. Based on the value of the current field, these fields could be shown or hidden."
          />

          {isValidArray(impactedByList) ? (
            <>
              {impactedByList
                .slice(0, 3)
                .map((label: string, index: number) => (
                  <Box key={index} pb="xs">
                    {index + 1}. {label}
                  </Box>
                ))}
              {impactedByList.length > 3 ? (
                <Box pb="xs">...and {impactedByList.length - 3} more.</Box>
              ) : null}
            </>
          ) : (
            <Text>
              None. No other fields on this form have visibility rules for this
              field.
            </Text>
          )}
        </Box>

        <Divider />

        <Box>
          <ContentTitle text="Rules for Current Field" />
          <Description
            size="xs"
            mt={0}
            mb="md"
            text="Configure visibility rules to show or hide the current field based on the target field values. If no rules are defined, the default visibility setting of the field is used. If multiple visibility rules are defined, they are evaluated in order and the first matching rule determines the field's visibility."
          />
          <Grid gutter="sm">
            {rules &&
              rules.length > 0 &&
              rules.map((item, index) => {
                // Find the corresponding V2 field for the selected field
                const selectedFieldV2 = formFieldsV2.find(
                  f => f.invariantId === item.targetFieldInvariantId
                );

                const isValidRule = validRules.some(
                  r => r.offlineId === item.offlineId
                );

                return (
                  <Grid.Col
                    span={{base: 12, lg: 6, xl: 4}}
                    key={item.offlineId}>
                    <Card key={item.offlineId} withBorder mt="md">
                      <Stack>
                        <Group justify="space-between">
                          <Group gap="xs">
                            {isValidRule ? (
                              <>
                                <SuccessIcon />
                                <Text size="xs" c="green">
                                  Rule Valid
                                </Text>
                              </>
                            ) : (
                              <>
                                <PendingIcon />
                                <Text size="xs" c="dimmed">
                                  Choose a field and value to validate rule
                                </Text>
                              </>
                            )}
                          </Group>
                          <DeleteButton
                            onClick={() => handleRemoveRule(item)}
                            size="sm">
                            Delete Visibility Rule
                          </DeleteButton>
                        </Group>

                        <SelectBox
                          label="When Field"
                          options={fieldListOptions.filter(
                            element =>
                              element.label !== field.label.abbreviate(40)
                          )}
                          value={item.targetFieldInvariantId}
                          onSelectChangeCallback={value =>
                            handleFieldNameChangeForRule(
                              value,
                              item.offlineId,
                              index
                            )
                          }
                        />

                        {selectedFieldV2 ? (
                          <>
                            <Grid style={{width: '100%'}} m={0}>
                              <FormField
                                field={{
                                  ...toSingleValueField(selectedFieldV2),
                                  label: `Has Value (Choose value for: ${selectedFieldV2.label})`,
                                }}
                                value={item.fieldValue}
                                onChange={(_, value) =>
                                  handleFieldValueChangeForRule(_, value, index)
                                }
                              />
                            </Grid>

                            <SelectBox
                              label="Then Show/Hide"
                              options={showHideOptions}
                              value={item.visibility ? 'show' : 'hide'}
                              onSelectChangeCallback={value =>
                                handleVisibilityChangeForRule(
                                  value,
                                  item.offlineId,
                                  index
                                )
                              }
                            />
                          </>
                        ) : (
                          <Text c="dimmed" size="xs">
                            Select a field to continue.
                          </Text>
                        )}
                      </Stack>
                    </Card>
                  </Grid.Col>
                );
              })}
          </Grid>
          {!rules ||
            (rules.length === 0 && (
              <Text>
                None. There are currently no visibility rules for this field.
              </Text>
            ))}
          <CreateButton size="sm" mt="md" onClick={handleAddRule}>
            Add Visibility Rule
          </CreateButton>
        </Box>

        <Group justify="flex-end" mt="xl">
          <Button onClick={handleCloseDialog} variant="subtle" color="black">
            Cancel
          </Button>

          <Button onClick={handleSaveSelections} aria-label="dialogOkBtn">
            Save
          </Button>
        </Group>
      </Modal>
    </Box>
  );
});
